import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Building2, Search } from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ViewToggle, { useViewMode } from '@/components/ui/ViewToggle';
import { qk, fetchClients } from '@/lib/queries';
import { useCan } from '@/hooks/use-scopes';

export default function ClientsListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: qk.clients, queryFn: fetchClients });
  const [view, setView] = useViewMode('clients', 'cards');
  const [q, setQ] = useState('');
  const canWrite = useCan('clients.write');

  const filtered = useMemo(() => {
    const list = data ?? [];
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((c) =>
      c.name.toLowerCase().includes(needle) ||
      c.vertical.toLowerCase().includes(needle),
    );
  }, [data, q]);

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle="Brands KAF PRO produces content for"
        actions={
          <div className="flex items-center gap-2">
            <ViewToggle mode={view} onChange={setView} />
            {canWrite && <Link to="/clients/new"><Button icon={<Plus size={14} />}>New client</Button></Link>}
          </div>
        }
      />

      {(data?.length ?? 0) > 0 && (
        <div className="relative mb-4 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients by name or vertical..."
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-line bg-bg-card text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/40"
          />
        </div>
      )}

      {isLoading && <div className="text-muted text-sm">Loading...</div>}

      {!isLoading && (data?.length ?? 0) === 0 && (
        <Card>
          <div className="text-center py-8 space-y-3">
            <Building2 className="mx-auto text-muted" size={36} />
            <p className="text-muted text-sm">No clients yet.</p>
            {canWrite
              ? <Link to="/clients/new"><Button>Create your first client</Button></Link>
              : <p className="text-xs text-muted/70">A clinic lead or an admin adds clients.</p>}
          </div>
        </Card>
      )}

      {!isLoading && (data?.length ?? 0) > 0 && filtered.length === 0 && (
        <Card>
          <div className="text-center py-6 text-sm text-muted">No clients match "{q}".</div>
        </Card>
      )}

      {!isLoading && filtered.length > 0 && view === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <Link key={c.id} to={`/clients/${c.id}`}>
              <Card className="hover:border-accent-400/50 transition cursor-pointer h-full">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-600/20 flex items-center justify-center text-accent-400">
                    {c.logo_url
                      ? <img src={c.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      : <Building2 size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-muted-strong truncate">{c.name}</div>
                    <div className="text-xs text-muted capitalize">{c.vertical.replace('_', ' ')}</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-muted">
                  {c.taxonomy.primary.label}: {c.taxonomy.primary.values.length} values
                  &middot; {c.talents.length} {c.taxonomy.talent.label.toLowerCase()}{c.talents.length === 1 ? '' : 's'}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {!isLoading && filtered.length > 0 && view === 'table' && (
        <Card>
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-line">
                  <th className="font-medium py-2 pr-3 w-12"></th>
                  <th className="font-medium py-2 pr-3">Name</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap">Vertical</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">{/* dynamic primary label */}Primary</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden lg:table-cell">Secondary</th>
                  <th className="font-medium py-2 whitespace-nowrap text-right">Talents</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-bg-soft transition"
                  >
                    <td className="py-2.5 pr-3">
                      <div className="w-8 h-8 rounded-lg bg-accent-600/20 flex items-center justify-center text-accent-400 shrink-0">
                        {c.logo_url
                          ? <img src={c.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                          : <Building2 size={14} />}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-muted-strong">{c.name}</td>
                    <td className="py-2.5 pr-3 text-xs text-muted capitalize whitespace-nowrap">{c.vertical.replace('_', ' ')}</td>
                    <td className="py-2.5 pr-3 text-xs text-muted whitespace-nowrap hidden md:table-cell">
                      <span className="text-muted-strong">{c.taxonomy.primary.label}</span>
                      <span className="text-muted/70 ml-1">({c.taxonomy.primary.values.length})</span>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-muted whitespace-nowrap hidden lg:table-cell">
                      <span className="text-muted-strong">{c.taxonomy.secondary.label}</span>
                      <span className="text-muted/70 ml-1">({c.taxonomy.secondary.values.length})</span>
                    </td>
                    <td className="py-2.5 text-right text-xs text-muted whitespace-nowrap">
                      {c.taxonomy.talent.enabled
                        ? <><span className="text-muted-strong">{c.talents.length}</span> <span className="text-muted/70">{c.taxonomy.talent.label.toLowerCase()}{c.talents.length === 1 ? '' : 's'}</span></>
                        : <span className="text-muted/60">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
